import { getLogger } from '@logtape/logtape';
import { useState } from 'react';
import { mutate } from 'swr';

import { DocumentType } from '@/models/documents/Document.ts';

import { uploadDocumentForUser } from '@/services/documentsService.ts';
import { showErrorNotification, showSuccessNotification } from '@/services/notificationService.ts';

import { validateFile } from '@/utils/file-utils.ts';

const logger = getLogger(['hook', 'file-upload']);

interface UseFileUploadReturn {
    isUploading: boolean;
    uploadFile: (file: File, documentType: DocumentType) => Promise<void>;
    validateFile: (file: File) => string | undefined;
}

interface UseFileUploadParams {
    userId: string;
    appointmentId?: string;
    instanceId?: string;
    swrKey?: unknown[];
    onUploadSuccess?: () => void | Promise<void>;
}

export const useFileUpload = ({
    userId,
    appointmentId,
    instanceId,
    swrKey,
    onUploadSuccess,
}: UseFileUploadParams): UseFileUploadReturn => {
    const [isUploading, setIsUploading] = useState(false);

    const uploadFile = async (file: File, documentType: DocumentType): Promise<void> => {
        const validationError = validateFile(file);
        if (validationError) {
            showErrorNotification(validationError);
            return;
        }

        setIsUploading(true);

        let success = false;

        try {
            await uploadDocumentForUser({
                userId,
                file,
                documentType,
                appointmentId,
                instanceId,
            });
            success = true;
        } catch {
            showErrorNotification('Beim Hochladen ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.');
        }

        setIsUploading(false);

        if (success) {
            showSuccessNotification('Dokument erfolgreich hochgeladen.');
            if (swrKey) {
                try {
                    await mutate(swrKey);
                } catch {
                    logger.warn('SWR revalidation failed after upload');
                }
            }
            if (onUploadSuccess) {
                try {
                    await onUploadSuccess();
                } catch {
                    logger.warn('Post-upload callback failed');
                }
            }
        }
    };

    return {
        isUploading,
        uploadFile,
        validateFile,
    };
};
